import {useCallback, useEffect, useRef} from 'react';
import {useSelector} from 'react-redux';
import {useIntl} from 'react-intl';
import {useAppDispatch, type RootState} from '../../store/store';
import {setDialerHidden} from '../../store/features/calls/dialerSlice';
import {selectTenant, selectUserId} from '../../store/features/Auth/authSlice';
import {useCallBlock, useGroupCallState, useRtcFeatures} from '../groupCalls/hooks/useGroupCall';
import {CallBlockCard} from './calls/CallBlockCard';
import {useCallEscalationListener} from './matrix/useCallEscalationListener';
import {useCallLegs, useCallSurfaceView, useEndedCallTracker} from './hooks/useCallSurface';
import {dismissEnded, selectCallSurfaceState, selectStartCallPillSuppressed} from './store/callSurfaceSlice';
import {StartCallPill} from './components/StartCallPill';
import {DockedCard} from './components/DockedCard';
import {CallBubble} from './components/CallBubble';
import {DialerBubble} from './dialer/DialerBubble';
import {useDialerWidth} from './dialer/dialerWidth';
import {useDialerActions} from './dialer/useDialerActions';
import {useTelephonyRuntime} from './hooks/useTelephonyRuntime';
import {useDialerRouting} from './hooks/useDialerRouting';
import {PhoneCallSurface} from './phone/PhoneCallSurface';
import {EndedCard} from './bubble/EndedCard';
import {GroupEndedStage} from './bubble/GroupEndedStage';
import {CallSurfaceBoundary} from './components/CallSurfaceBoundary';
import {GroupCallHost} from './group/GroupCallHost';
import {GroupSurfaces} from './group/GroupSurfaces';
import {MatrixCallSurface} from './matrix/MatrixCallSurface';
import {DndCallDialog} from './matrix/DndCallDialog';
import {DirectPrep} from './prep/DirectPrep';
import {startInternalCall} from './matrix/matrixActions';
import {IncomingManyCard, WaitingCard} from './bubble/WaitingCards';
import type {HeldCall} from './components/HeldCallRow';
import {useLegActions, type LegActions} from './hooks/useLegActions';
import {layoutOf, type SurfaceLayout} from './model/layout';
import {legNameOf} from './model/legName';
import type {CallLeg, EndedCall} from './model/types';
import {callSurfaceMessages as m} from './messages';
import './callTokens.css';
import {CallLayer} from './components/CallLayer';

const legOf = <E extends CallLeg['engine']>(legs: readonly CallLeg[], engine: E): Extract<CallLeg, {engine: E}> | undefined =>
    legs.find(leg => leg.engine === engine) as Extract<CallLeg, {engine: E}> | undefined;

const heldCallOf = (layout: SurfaceLayout, actions: LegActions): HeldCall | undefined => {
    const [primary] = layout.legs;
    const held = layout.held;
    if (!primary || !held) {
        return undefined;
    }
    return {
        leg: held,
        onSwap: () => {
            actions.setHold(primary, true);
            actions.setHold(held, false);
        },
    };
};

export const CallSurfaceRoot = ({matrixEnabled}: {matrixEnabled: boolean}) => {
    const intl = useIntl();
    const dialerWidth = useDialerWidth();
    const dispatch = useAppDispatch();
    useTelephonyRuntime();
    useDialerRouting();
    const legs = useCallLegs();
    const {callNumber} = useDialerActions();
    useEndedCallTracker(legs);
    const view = useCallSurfaceView(legs);
    const block = useCallBlock();
    const pillRef = useRef<HTMLButtonElement>(null);
    const dialerRef = useRef<HTMLElement>(null);
    const restorePillFocus = useRef(false);
    const {dndPrompt, expanded, directPrep} = useSelector(selectCallSurfaceState);
    const rtcFeatures = useRtcFeatures();
    useCallEscalationListener(matrixEnabled && rtcFeatures.groupCalls, expanded);
    const legActions = useLegActions();
    const callState = useSelector((state: RootState) => state.dialer.callState);
    const currentUserId = useSelector(selectUserId);
    const pillSuppressed = useSelector(selectStartCallPillSuppressed);
    const tenant = useSelector(selectTenant);

    const showDialer = view.kind === 'dialer';
    const groupPhase = useGroupCallState().phase;
    const showPill = view.kind === 'idle' && (groupPhase === 'idle' || groupPhase === 'ended') && !dndPrompt && !pillSuppressed;
    const layout = layoutOf(view, expanded);
    const groupLeg = legOf(layout.legs, 'group');
    const matrixLeg = legOf(layout.legs, 'matrix');
    const phoneLeg = legOf(layout.legs, 'sip');
    const held = heldCallOf(layout, legActions);

    const openDialer = useCallback(() => {
        dispatch(setDialerHidden(false));
    }, [dispatch]);

    const closeDialer = useCallback(() => {
        restorePillFocus.current = true;
        dispatch(setDialerHidden(true));
    }, [dispatch]);

    const closeEnded = useCallback(() => {
        dispatch(dismissEnded(view.kind === 'ended' ? view.ended.legId : undefined));
    }, [dispatch, view]);

    const callBack = (ended: EndedCall) => {
        const target = ended.callBack;
        dispatch(dismissEnded(ended.legId));
        if (target?.kind === 'internal') {
            void startInternalCall(target.peer, {
                video: target.video,
                callState,
                currentUserId: String(currentUserId ?? ''),
                tenant: String(tenant ?? ''),
                dispatch,
            });
        } else if (target?.kind === 'phone') {
            callNumber(target.number);
        }
    };

    useEffect(() => {
        if (showPill && restorePillFocus.current) {
            restorePillFocus.current = false;
            pillRef.current?.focus();
        }
    }, [showPill]);

    useEffect(() => {
        if (!showDialer) {
            return;
        }
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && dialerRef.current?.contains(event.target as Node)) {
                closeDialer();
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [closeDialer, showDialer]);

    return (
        <GroupCallHost enabled={matrixEnabled}>
            <CallLayer>
                {matrixEnabled && <GroupSurfaces leg={groupLeg} expanded={view.kind === 'call' ? view.expanded : false}/>}
                {matrixEnabled && matrixLeg && <MatrixCallSurface key={matrixLeg.id} leg={matrixLeg} held={held}/>}
                {phoneLeg && <PhoneCallSurface key={phoneLeg.id} leg={phoneLeg} held={held}/>}
                {layout.waiting && (
                    <CallSurfaceBoundary resetKey={layout.waiting.incoming.id}>
                        <CallBubble
                            tone="call"
                            urgent
                            label={intl.formatMessage(m.waitingRegion, {name: legNameOf(layout.waiting.current, intl)})}
                            testId="call-waiting-bubble"
                        >
                            <WaitingCard incoming={layout.waiting.incoming} current={layout.waiting.current} actions={legActions}/>
                        </CallBubble>
                    </CallSurfaceBoundary>
                )}
                {layout.many && (
                    <CallSurfaceBoundary resetKey={layout.many.map(leg => leg.id).join('|')}>
                        <CallBubble tone="call" urgent label={intl.formatMessage(m.manyRegion, {count: layout.many.length})} testId="call-incoming-many-bubble">
                            <IncomingManyCard incoming={layout.many} actions={legActions}/>
                        </CallBubble>
                    </CallSurfaceBoundary>
                )}
                {showDialer && (
                    <CallSurfaceBoundary resetKey="dialer">
                        <CallBubble ref={dialerRef} tone="dialer" width={dialerWidth} label={intl.formatMessage(m.dialerRegion)} testId="dialer-bubble">
                            <DialerBubble onClose={closeDialer}/>
                        </CallBubble>
                    </CallSurfaceBoundary>
                )}
                {view.kind === 'ended' && view.ended.everyoneLeftOnStage && (
                    <CallSurfaceBoundary resetKey={view.ended.legId}>
                        <GroupEndedStage ended={view.ended} onClose={closeEnded}/>
                    </CallSurfaceBoundary>
                )}
                {view.kind === 'ended' && !view.ended.everyoneLeftOnStage && (
                    <CallSurfaceBoundary resetKey={view.ended.legId}>
                        <CallBubble
                            tone="call"
                            label={intl.formatMessage(view.ended.kind === 'group' ? m.groupEndedRegion : m.endedRegion, {name: view.ended.identity.name ?? view.ended.identity.number ?? ''})}
                            testId="ended-bubble"
                        >
                            <EndedCard
                                ended={view.ended}
                                onClose={closeEnded}
                                onCallBack={view.ended.callBack ? () => callBack(view.ended) : undefined}
                            />
                        </CallBubble>
                    </CallSurfaceBoundary>
                )}
                {dndPrompt && <DndCallDialog/>}
                {directPrep && <DirectPrep key={directPrep.roomId} request={directPrep} videoAvailable={rtcFeatures.videoCalls}/>}
                {showPill && <StartCallPill ref={pillRef} onOpen={openDialer}/>}
                {block?.origin === 'surface' && (
                    <DockedCard width={360}>
                        <CallBlockCard block={block}/>
                    </DockedCard>
                )}
            </CallLayer>
        </GroupCallHost>
    );
};
